/**
 * CVX vaccine codes and the CDC vaccine groups they belong to.
 *
 * Transcribed from the CDC IIS "CVX to Vaccine Group" mapping and the CVX code list. Group codes are
 * the CDC's own (`FLU`, `COVID-19`, `PneumoPCV`, …) so the table is mechanical rather than curated;
 * combination products list every component group (DTaP-HepB-IPV is `DTAP`, `HepB` and `POLIO`),
 * which the CDC mapping also does by repeating the row. Display grouping and ordering are layered on
 * top of these keys in `immunizationRecords.ts`.
 *
 * `shortName` is one canonical line per code, so the same product reads the same however the
 * source spelled its display. `unspecified` marks the "unspecified formulation" codes an exporter
 * emits alongside the specific product for the same shot.
 */

export const CVX_SYSTEM = 'http://hl7.org/fhir/sid/cvx';

export const VACCINE_GROUPS = [
  'FLU',
  'COVID-19',
  'TDAP',
  'Td',
  'PneumoPCV',
  'PneumoPPV',
  'ZOSTER',
  'RSV',
  'HepB',
  'HepA',
  'MMR',
  'VARICELLA',
  'HPV',
  'MENING',
  'MeningB',
  'DTAP',
  'HIB',
  'POLIO',
  'ROTAVIRUS',
  'ADENO',
  'ANTHRAX',
  'BCG',
  'Chikungunya',
  'cholera',
  'DENGUE',
  'Ebola',
  'FLU-Avian-H5',
  'H1N1 flu',
  'H5N1 flu',
  'Japanese encephalitis',
  'Lyme disease',
  'Orthopoxvirus',
  'Plague',
  'RABIES',
  'Tick-borne encephalitis',
  'TYPHOID',
  'VEE',
  'YELLOWFEVER',
] as const;

/** A CDC vaccine group code. */
export type VaccineGroup = (typeof VACCINE_GROUPS)[number];

/** CVX codes that are not vaccines: skin tests and passive immunity products. */
export const NON_VACCINE_GROUPS = ['TST', 'IG'] as const;

export type NonVaccineGroup = (typeof NON_VACCINE_GROUPS)[number];

export type ImmunizationGroupCode = VaccineGroup | NonVaccineGroup;

export type CvxEntry = {
  groups: readonly ImmunizationGroupCode[];
  shortName: string;
  unspecified: boolean;
};

const entry = (
  groups: readonly ImmunizationGroupCode[],
  shortName: string,
  options: { unspecified?: boolean } = {},
): CvxEntry => ({ groups, shortName, unspecified: options.unspecified ?? false });

const flu = (shortName: string, unspecified = false): CvxEntry => entry(['FLU'], shortName, { unspecified });
const covid = (shortName: string, unspecified = false): CvxEntry => entry(['COVID-19'], shortName, { unspecified });

/** English group names, for hosts with no i18n tree (the AI context). UI hosts translate the key. */
export const VACCINE_GROUP_NAMES: Record<ImmunizationGroupCode, string> = {
  FLU: 'Influenza',
  'COVID-19': 'COVID-19',
  TDAP: 'Tdap',
  Td: 'Td',
  PneumoPCV: 'Pneumococcal conjugate',
  PneumoPPV: 'Pneumococcal polysaccharide',
  ZOSTER: 'Zoster (shingles)',
  RSV: 'RSV',
  HepB: 'Hepatitis B',
  HepA: 'Hepatitis A',
  MMR: 'MMR',
  VARICELLA: 'Varicella',
  HPV: 'HPV',
  MENING: 'Meningococcal ACWY',
  MeningB: 'Meningococcal B',
  DTAP: 'DTaP',
  HIB: 'Hib',
  POLIO: 'Polio',
  ROTAVIRUS: 'Rotavirus',
  ADENO: 'Adenovirus',
  ANTHRAX: 'Anthrax',
  BCG: 'BCG',
  Chikungunya: 'Chikungunya',
  cholera: 'Cholera',
  DENGUE: 'Dengue',
  Ebola: 'Ebola',
  'FLU-Avian-H5': 'Avian influenza H5',
  'H1N1 flu': 'H1N1 influenza',
  'H5N1 flu': 'H5N1 influenza',
  'Japanese encephalitis': 'Japanese encephalitis',
  'Lyme disease': 'Lyme disease',
  Orthopoxvirus: 'Smallpox / mpox',
  Plague: 'Plague',
  RABIES: 'Rabies',
  'Tick-borne encephalitis': 'Tick-borne encephalitis',
  TYPHOID: 'Typhoid',
  VEE: 'Venezuelan equine encephalitis',
  YELLOWFEVER: 'Yellow fever',
  TST: 'Tuberculin skin test',
  IG: 'Immune globulin',
};

export const CVX_VACCINES: Record<string, CvxEntry> = {
  // Influenza
  '15': flu('Influenza, split virus'),
  '16': flu('Influenza, whole virus'),
  '88': flu('Influenza', true),
  '111': flu('Influenza, live nasal, trivalent'),
  '135': flu('Influenza, high-dose, trivalent'),
  '140': flu('Influenza, trivalent'),
  '141': flu('Influenza, trivalent'),
  '144': flu('Influenza, intradermal, trivalent'),
  '149': flu('Influenza, live nasal, quadrivalent'),
  '150': flu('Influenza, quadrivalent'),
  '151': flu('Influenza, live nasal', true),
  '153': flu('Influenza, cell-based, trivalent'),
  '155': flu('Influenza, recombinant, trivalent'),
  '158': flu('Influenza, quadrivalent'),
  '161': flu('Influenza, quadrivalent, pediatric'),
  '166': flu('Influenza, intradermal, quadrivalent'),
  '168': flu('Influenza, adjuvanted, trivalent'),
  '171': flu('Influenza, cell-based, quadrivalent'),
  '185': flu('Influenza, recombinant, quadrivalent'),
  '186': flu('Influenza, cell-based, quadrivalent'),
  '194': flu('Influenza, Southern Hemisphere', true),
  '197': flu('Influenza, high-dose, quadrivalent'),
  '200': flu('Influenza, Southern Hemisphere, pediatric'),
  '201': flu('Influenza, Southern Hemisphere, quadrivalent'),
  '202': flu('Influenza, Southern Hemisphere, quadrivalent'),
  '205': flu('Influenza, adjuvanted, quadrivalent'),
  '231': flu('Influenza, Southern Hemisphere, high-dose'),
  '320': flu('Influenza, cell-based, trivalent'),
  '331': flu('Influenza, Southern Hemisphere, trivalent'),
  '333': flu('Influenza, live nasal, trivalent'),
  '337': flu('Influenza, Southern Hemisphere, high-dose'),
  '338': flu('Influenza, mRNA, trivalent'),
  // Pandemic and avian influenza
  '123': entry(['H5N1 flu'], 'Influenza H5N1'),
  '160': entry(['H5N1 flu'], 'Influenza H5N1, adjuvanted'),
  '125': entry(['H1N1 flu'], 'Influenza H1N1 (2009), nasal'),
  '126': entry(['H1N1 flu'], 'Influenza H1N1 (2009)'),
  '127': entry(['H1N1 flu'], 'Influenza H1N1 (2009)'),
  '128': entry(['H1N1 flu'], 'Influenza H1N1 (2009)', { unspecified: true }),
  '321': entry(['FLU-Avian-H5'], 'Avian influenza H5N8'),
  '322': entry(['FLU-Avian-H5'], 'Avian influenza H5N8'),
  '323': entry(['FLU-Avian-H5'], 'Avian influenza H5', { unspecified: true }),
  // COVID-19
  '207': covid('COVID-19 mRNA (Moderna)'),
  '208': covid('COVID-19 mRNA (Pfizer-BioNTech)'),
  '210': covid('COVID-19 viral vector (AstraZeneca)'),
  '211': covid('COVID-19 protein subunit (Novavax)'),
  '212': covid('COVID-19 viral vector (Janssen)'),
  '213': covid('COVID-19', true),
  '217': covid('COVID-19 mRNA (Pfizer-BioNTech)'),
  '218': covid('COVID-19 mRNA (Pfizer-BioNTech), pediatric'),
  '219': covid('COVID-19 mRNA (Pfizer-BioNTech), pediatric'),
  '221': covid('COVID-19 mRNA (Moderna)'),
  '225': covid('COVID-19 protein subunit (Sanofi)'),
  '226': covid('COVID-19 protein subunit (Sanofi)'),
  '227': covid('COVID-19 mRNA (Moderna), pediatric'),
  '228': covid('COVID-19 mRNA (Moderna), pediatric'),
  '229': covid('COVID-19 mRNA bivalent (Moderna)'),
  '230': covid('COVID-19 mRNA bivalent (Moderna), pediatric'),
  '300': covid('COVID-19 mRNA bivalent (Pfizer-BioNTech)'),
  '301': covid('COVID-19 mRNA bivalent (Pfizer-BioNTech), pediatric'),
  '302': covid('COVID-19 mRNA bivalent (Pfizer-BioNTech), pediatric'),
  '308': covid('COVID-19 mRNA (Pfizer-BioNTech), pediatric'),
  '309': covid('COVID-19 mRNA (Pfizer-BioNTech)'),
  '310': covid('COVID-19 mRNA (Pfizer-BioNTech), pediatric'),
  '311': covid('COVID-19 mRNA (Moderna), pediatric'),
  '312': covid('COVID-19 mRNA (Moderna)'),
  '313': covid('COVID-19 protein subunit (Novavax)'),
  '334': covid('COVID-19 mRNA (Moderna)'),
  '500': covid('COVID-19, non-US', true),
  '501': covid('COVID-19 (QazCovid-in)'),
  '502': covid('COVID-19 (Covaxin)'),
  '503': covid('COVID-19 (CoviVac)'),
  '504': covid('COVID-19 (Sputnik Light)'),
  '505': covid('COVID-19 (Sputnik V)'),
  '506': covid('COVID-19 (Convidecia)'),
  '507': covid('COVID-19 (Zifivax)'),
  '508': covid('COVID-19 (Jiangsu CDC)'),
  '509': covid('COVID-19 (EpiVacCorona)'),
  '510': covid('COVID-19 (Sinopharm BIBP)'),
  '511': covid('COVID-19 (CoronaVac)'),
  '512': covid('COVID-19 (Covifenz)'),
  '513': covid('COVID-19 (Zifivax)'),
  '514': covid('COVID-19 (ZyCoV-D)'),
  '515': covid('COVID-19 (MVC-COV1901)'),
  '516': covid('COVID-19 (KconecaVac)'),
  '517': covid('COVID-19 (Corbevax)'),
  '518': covid('COVID-19 (Valneva)'),
  '519': covid('COVID-19 mRNA bivalent (Moderna)'),
  '520': covid('COVID-19 mRNA bivalent (Pfizer-BioNTech)'),
  '521': covid('COVID-19 protein subunit (VidPrevtyn Beta)'),
  // Tetanus, diphtheria, pertussis
  '115': entry(['TDAP'], 'Tdap'),
  '09': entry(['Td'], 'Td'),
  '35': entry(['Td'], 'Tetanus toxoid'),
  '112': entry(['Td'], 'Tetanus toxoid', { unspecified: true }),
  '113': entry(['Td'], 'Td'),
  '138': entry(['Td'], 'Td'),
  '139': entry(['Td'], 'Td', { unspecified: true }),
  '142': entry(['Td'], 'Tetanus toxoid'),
  '196': entry(['Td'], 'Td'),
  '01': entry(['DTAP'], 'DTP'),
  '20': entry(['DTAP'], 'DTaP'),
  '28': entry(['DTAP'], 'DT, pediatric'),
  '106': entry(['DTAP'], 'DTaP'),
  '107': entry(['DTAP'], 'DTaP', { unspecified: true }),
  '22': entry(['DTAP', 'HIB'], 'DTP-Hib'),
  '50': entry(['DTAP', 'HIB'], 'DTaP-Hib'),
  '102': entry(['DTAP', 'HIB', 'HepB'], 'DTP-Hib-HepB'),
  '110': entry(['DTAP', 'HepB', 'POLIO'], 'DTaP-HepB-IPV'),
  '120': entry(['DTAP', 'HIB', 'POLIO'], 'DTaP-Hib-IPV'),
  '130': entry(['DTAP', 'POLIO'], 'DTaP-IPV'),
  '132': entry(['DTAP', 'POLIO', 'HIB', 'HepB'], 'DTaP-IPV-Hib-HepB'),
  '146': entry(['DTAP', 'POLIO', 'HIB', 'HepB'], 'DTaP-IPV-Hib-HepB'),
  '170': entry(['DTAP', 'POLIO', 'HIB'], 'DTaP-IPV-Hib, non-US'),
  '195': entry(['DTAP', 'POLIO'], 'DT-IPV'),
  '198': entry(['DTAP', 'HepB', 'HIB'], 'DTP-HepB-Hib, non-US'),
  // Pneumococcal
  '33': entry(['PneumoPPV'], 'Pneumococcal polysaccharide (PPSV23)'),
  '100': entry(['PneumoPCV'], 'Pneumococcal conjugate (PCV7)'),
  '109': entry(['PneumoPCV'], 'Pneumococcal', { unspecified: true }),
  '133': entry(['PneumoPCV'], 'Pneumococcal conjugate (PCV13)'),
  '152': entry(['PneumoPCV'], 'Pneumococcal conjugate', { unspecified: true }),
  '177': entry(['PneumoPCV'], 'Pneumococcal conjugate (PCV10)'),
  '215': entry(['PneumoPCV'], 'Pneumococcal conjugate (PCV15)'),
  '216': entry(['PneumoPCV'], 'Pneumococcal conjugate (PCV20)'),
  '327': entry(['PneumoPCV'], 'Pneumococcal conjugate (PCV21)'),
  // Zoster
  '121': entry(['ZOSTER'], 'Zoster, live'),
  '187': entry(['ZOSTER'], 'Zoster, recombinant'),
  '188': entry(['ZOSTER'], 'Zoster', { unspecified: true }),
  // RSV
  '303': entry(['RSV'], 'RSV, recombinant (Arexvy)'),
  '304': entry(['RSV'], 'RSV', { unspecified: true }),
  '305': entry(['RSV'], 'RSV, bivalent (Abrysvo)'),
  '306': entry(['RSV'], 'RSV antibody (nirsevimab)'),
  '307': entry(['RSV'], 'RSV antibody (nirsevimab)'),
  '314': entry(['RSV'], 'RSV vaccine', { unspecified: true }),
  '315': entry(['RSV'], 'RSV antibody', { unspecified: true }),
  '326': entry(['RSV'], 'RSV, mRNA'),
  '332': entry(['RSV'], 'RSV antibody (clesrovimab)'),
  '71': entry(['RSV'], 'RSV immune globulin'),
  '93': entry(['RSV'], 'RSV antibody (palivizumab)'),
  '145': entry(['RSV'], 'RSV antibody (motavizumab)'),
  // Hepatitis B
  '08': entry(['HepB'], 'Hepatitis B, pediatric'),
  '42': entry(['HepB'], 'Hepatitis B, adolescent'),
  '43': entry(['HepB'], 'Hepatitis B, adult'),
  '44': entry(['HepB'], 'Hepatitis B, high-dose'),
  '45': entry(['HepB'], 'Hepatitis B', { unspecified: true }),
  '51': entry(['HIB', 'HepB'], 'Hib-HepB'),
  '189': entry(['HepB'], 'Hepatitis B (Heplisav-B)'),
  '220': entry(['HepB'], 'Hepatitis B, 3-antigen'),
  // Hepatitis A
  '31': entry(['HepA'], 'Hepatitis A, pediatric', { unspecified: true }),
  '52': entry(['HepA'], 'Hepatitis A, adult'),
  '83': entry(['HepA'], 'Hepatitis A, pediatric'),
  '84': entry(['HepA'], 'Hepatitis A, pediatric'),
  '85': entry(['HepA'], 'Hepatitis A', { unspecified: true }),
  '104': entry(['HepA', 'HepB'], 'Hepatitis A and B'),
  '169': entry(['HepA'], 'Hepatitis A, live'),
  '193': entry(['HepA', 'HepB'], 'Hepatitis A and B, pediatric'),
  // Measles, mumps, rubella, varicella
  '03': entry(['MMR'], 'MMR'),
  '04': entry(['MMR'], 'Measles and rubella'),
  '05': entry(['MMR'], 'Measles'),
  '06': entry(['MMR'], 'Rubella'),
  '07': entry(['MMR'], 'Mumps'),
  '38': entry(['MMR'], 'Rubella and mumps'),
  '94': entry(['MMR', 'VARICELLA'], 'MMRV'),
  '21': entry(['VARICELLA'], 'Varicella'),
  // HPV
  '62': entry(['HPV'], 'HPV, quadrivalent'),
  '118': entry(['HPV'], 'HPV, bivalent'),
  '137': entry(['HPV'], 'HPV', { unspecified: true }),
  '165': entry(['HPV'], 'HPV, 9-valent'),
  // Meningococcal
  '32': entry(['MENING'], 'Meningococcal polysaccharide (MPSV4)'),
  '103': entry(['MENING'], 'Meningococcal C conjugate'),
  '108': entry(['MENING'], 'Meningococcal ACWY', { unspecified: true }),
  '114': entry(['MENING'], 'Meningococcal ACWY (Menactra)'),
  '136': entry(['MENING'], 'Meningococcal ACWY (Menveo)'),
  '147': entry(['MENING'], 'Meningococcal ACWY conjugate', { unspecified: true }),
  '148': entry(['MENING', 'HIB'], 'Meningococcal C/Y and Hib'),
  '167': entry(['MENING'], 'Meningococcal', { unspecified: true }),
  '191': entry(['MENING'], 'Meningococcal A polysaccharide'),
  '192': entry(['MENING'], 'Meningococcal AC polysaccharide'),
  '203': entry(['MENING'], 'Meningococcal ACWY (MenQuadfi)'),
  '162': entry(['MeningB'], 'Meningococcal B (Trumenba)'),
  '163': entry(['MeningB'], 'Meningococcal B (Bexsero)'),
  '164': entry(['MeningB'], 'Meningococcal B', { unspecified: true }),
  '316': entry(['MENING', 'MeningB'], 'Meningococcal ABCWY (Penbraya)'),
  '328': entry(['MENING', 'MeningB'], 'Meningococcal ABCWY (Penmenvy)'),
  // Hib
  '17': entry(['HIB'], 'Hib', { unspecified: true }),
  '46': entry(['HIB'], 'Hib (PRP-D)'),
  '47': entry(['HIB'], 'Hib (HbOC)'),
  '48': entry(['HIB'], 'Hib (PRP-T)'),
  '49': entry(['HIB'], 'Hib (PRP-OMP)'),
  // Polio
  '02': entry(['POLIO'], 'Polio, oral (OPV)'),
  '10': entry(['POLIO'], 'Polio, inactivated (IPV)'),
  '89': entry(['POLIO'], 'Polio', { unspecified: true }),
  '178': entry(['POLIO'], 'Polio, oral bivalent'),
  '179': entry(['POLIO'], 'Polio, oral monovalent', { unspecified: true }),
  '182': entry(['POLIO'], 'Polio, oral', { unspecified: true }),
  '324': entry(['POLIO'], 'Polio, inactivated, fractional dose'),
  // Rotavirus
  '74': entry(['ROTAVIRUS'], 'Rotavirus, tetravalent'),
  '116': entry(['ROTAVIRUS'], 'Rotavirus, pentavalent (RotaTeq)'),
  '119': entry(['ROTAVIRUS'], 'Rotavirus, monovalent (Rotarix)'),
  '122': entry(['ROTAVIRUS'], 'Rotavirus', { unspecified: true }),
  // Travel and other
  '54': entry(['ADENO'], 'Adenovirus type 4'),
  '55': entry(['ADENO'], 'Adenovirus type 7'),
  '82': entry(['ADENO'], 'Adenovirus', { unspecified: true }),
  '143': entry(['ADENO'], 'Adenovirus types 4 and 7'),
  '24': entry(['ANTHRAX'], 'Anthrax'),
  '318': entry(['ANTHRAX'], 'Anthrax, post-exposure'),
  '319': entry(['ANTHRAX'], 'Anthrax', { unspecified: true }),
  '19': entry(['BCG'], 'BCG'),
  '317': entry(['Chikungunya'], 'Chikungunya, live'),
  '329': entry(['Chikungunya'], 'Chikungunya, VLP'),
  '26': entry(['cholera'], 'Cholera', { unspecified: true }),
  '172': entry(['cholera'], 'Cholera, WC-rBS'),
  '173': entry(['cholera'], 'Cholera, BivWC'),
  '174': entry(['cholera'], 'Cholera, live'),
  '56': entry(['DENGUE'], 'Dengue, tetravalent'),
  '330': entry(['DENGUE'], 'Dengue', { unspecified: true }),
  '204': entry(['Ebola'], 'Ebola Zaire'),
  '214': entry(['Ebola'], 'Ebola', { unspecified: true }),
  '39': entry(['Japanese encephalitis'], 'Japanese encephalitis, SC'),
  '129': entry(['Japanese encephalitis'], 'Japanese encephalitis', { unspecified: true }),
  '134': entry(['Japanese encephalitis'], 'Japanese encephalitis, IM'),
  '66': entry(['Lyme disease'], 'Lyme disease'),
  '75': entry(['Orthopoxvirus'], 'Smallpox / mpox, live (ACAM2000)'),
  '105': entry(['Orthopoxvirus'], 'Smallpox, diluted'),
  '206': entry(['Orthopoxvirus'], 'Smallpox / mpox (Jynneos)'),
  '325': entry(['Orthopoxvirus'], 'Smallpox / mpox', { unspecified: true }),
  '23': entry(['Plague'], 'Plague'),
  '18': entry(['RABIES'], 'Rabies, IM'),
  '40': entry(['RABIES'], 'Rabies, intradermal'),
  '90': entry(['RABIES'], 'Rabies', { unspecified: true }),
  '175': entry(['RABIES'], 'Rabies, IM (diploid cell)'),
  '176': entry(['RABIES'], 'Rabies, IM (fibroblast)'),
  '77': entry(['Tick-borne encephalitis'], 'Tick-borne encephalitis, non-US'),
  '222': entry(['Tick-borne encephalitis'], 'Tick-borne encephalitis', { unspecified: true }),
  '223': entry(['Tick-borne encephalitis'], 'Tick-borne encephalitis, pediatric'),
  '224': entry(['Tick-borne encephalitis'], 'Tick-borne encephalitis, adult'),
  '25': entry(['TYPHOID'], 'Typhoid, oral'),
  '41': entry(['TYPHOID'], 'Typhoid, parenteral'),
  '53': entry(['TYPHOID'], 'Typhoid, parenteral (AKD)'),
  '91': entry(['TYPHOID'], 'Typhoid', { unspecified: true }),
  '101': entry(['TYPHOID'], 'Typhoid, Vi polysaccharide'),
  '190': entry(['TYPHOID'], 'Typhoid conjugate'),
  '80': entry(['VEE'], 'Venezuelan equine encephalitis, live'),
  '81': entry(['VEE'], 'Venezuelan equine encephalitis, inactivated'),
  '92': entry(['VEE'], 'Venezuelan equine encephalitis', { unspecified: true }),
  '37': entry(['YELLOWFEVER'], 'Yellow fever'),
  '183': entry(['YELLOWFEVER'], 'Yellow fever'),
  '184': entry(['YELLOWFEVER'], 'Yellow fever', { unspecified: true }),
  // Tuberculin skin tests
  '95': entry(['TST'], 'Tuberculin skin test, tine'),
  '96': entry(['TST'], 'Tuberculin skin test (PPD)'),
  '97': entry(['TST'], 'Tuberculin skin test (PPD), tine'),
  '98': entry(['TST'], 'Tuberculin skin test', { unspecified: true }),
  // Immune globulins and antitoxins
  '12': entry(['IG'], 'Diphtheria antitoxin'),
  '13': entry(['IG'], 'Tetanus immune globulin'),
  '14': entry(['IG'], 'Immune globulin', { unspecified: true }),
  '27': entry(['IG'], 'Botulinum antitoxin'),
  '29': entry(['IG'], 'CMV immune globulin'),
  '30': entry(['IG'], 'Hepatitis B immune globulin'),
  '34': entry(['IG'], 'Rabies immune globulin'),
  '36': entry(['IG'], 'Varicella zoster immune globulin'),
  '79': entry(['IG'], 'Vaccinia immune globulin'),
  '86': entry(['IG'], 'Immune globulin, IM'),
  '87': entry(['IG'], 'Immune globulin, IV'),
  '117': entry(['IG'], 'Varicella zoster immune globulin'),
  '154': entry(['IG'], 'Hepatitis A immune globulin'),
  '156': entry(['IG'], 'Rho(D) immune globulin'),
  '157': entry(['IG'], 'Rho(D) immune globulin, IM'),
  '159': entry(['IG'], 'Rho(D) immune globulin', { unspecified: true }),
  '180': entry(['IG'], 'Tetanus immune globulin'),
  '181': entry(['IG'], 'Anthrax immune globulin'),
};

/** CVX codes that record the absence of a vaccine rather than one: nothing to display. */
export const CVX_PLACEHOLDER_CODES = new Set(['998', '999', '99']);

export const getCvxEntry = (code: string | undefined): CvxEntry | undefined =>
  code === undefined ? undefined : CVX_VACCINES[code];
